import type { EntityFilter } from 'App/pages/AdvancedSearch/AdvancedSearchEntities/EntityFilter';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import ProjectResource from 'App/resources/Project';
import type Project from 'App/resources/Model/Project';
import ProductResource from 'App/babel/resources/Product';
import ProductAttributes from 'App/babel/attributes/ProductAttributes';
import type Product from 'App/resources/Model/Product';
import { getRolesFromState, getUsersFromState } from 'Store/selectors/AppSelectors';
import type { AppState } from 'Store/reducers/AppReducer';
import t from 'Components/Helpers/translate';
import UserRoleTree from 'Components/Helpers/UserRoleTree';
import AppService from 'App/legacy/AppService';
import { findAllChildren } from 'App/babel/angular/attributes/filter/treeHelpers';
import type { ListViewFilter } from 'App/resources/AllIWant';

export const getHasFieldSelectOptions = () => [
	{ value: 'all' as const, title: t('filters.all'), inactive: true },
	{ value: true, title: t('default.has') },
	{ value: false, title: t('default.hasNot') }
];

export type FilterNameTypes = 'ACCOUNT' | 'CONTACT' | 'ACTIVITY' | 'APPOINTMENT' | 'OPPORTUNITY' | 'ORDER' | 'ORDERROW';

export enum AdvancedSearchType {
	accounts = 'accounts',
	contacts = 'contacts',
	activities = 'activities',
	appointments = 'appointments',
	opportunities = 'opportunities',
	orders = 'orders'
}

export const filterNameMap = {
	[AdvancedSearchType.accounts]: 'ACCOUNT',
	[AdvancedSearchType.contacts]: 'CONTACT',
	[AdvancedSearchType.activities]: 'ACTIVITY',
	[AdvancedSearchType.appointments]: 'APPOINTMENT',
	[AdvancedSearchType.opportunities]: 'OPPORTUNITY',
	[AdvancedSearchType.orders]: 'ORDER'
} as const;

export const customFieldEntityMap = {
	[AdvancedSearchType.accounts]: 'account',
	[AdvancedSearchType.contacts]: 'contact',
	[AdvancedSearchType.activities]: 'activity',
	[AdvancedSearchType.appointments]: 'appointment',
	[AdvancedSearchType.opportunities]: 'opportunity',
	[AdvancedSearchType.orders]: 'order'
} as const;

export enum ListViewMap {
	accounts = 'advancedSearchAccount',
	contacts = 'advancedSearchContact',
	activities = 'advancedSearchActivity',
	appointments = 'advancedSearchAppointment',
	opportunities = 'advancedSearchOpportunity',
	orders = 'advancedSearchOrder'
}

export enum AdvancedSearchEntity {
	accounts = 'Client',
	contacts = 'Contact',
	activities = 'Activity',
	appointments = 'Appointment',
	opportunities = 'Opportunity',
	orders = 'Order'
}

export function getTypesObject<T>(value: T) {
	return (Object.keys(AdvancedSearchType) as AdvancedSearchType[]).reduce(
		(r, t) => ({ ...r, [t]: value }),
		{} as Record<AdvancedSearchType, T>
	);
}

const loggedInUserOption = () => ({ id: 'self' as string | number, title: t('default.loggedInUser') });

export const createUserSelectOptions = (accessType: string) => () => {
	const tree = UserRoleTree({
		accessType,
		roleIdPrefix: true,
		selectableRoles: true,
		formatUser: (u: any) => {
			u.title = u.name;
			return u;
		},
		formatRole: (r: any) => {
			r.title = r.name;
			return r;
		}
	});
	return [loggedInUserOption(), ...tree];
};

export const createUserFlatOptions = (accessType: string) => (App: AppState) => {
	const roleOptions = getRolesFromState(App, accessType).map(r => ({
		id: ('role-' + r.id) as string | number,
		title: r.name
	}));
	const userOptions = getUsersFromState(App, accessType).map(u => ({
		id: u.id as string | number,
		title: u.name
	}));
	return [loggedInUserOption(), ...roleOptions, ...userOptions];
};

export const createCampaignFetcher = () => (searchStr: string, ids?: number[]) => {
	const rb = new RequestBuilder();
	if (ids?.length) {
		rb.addFilter({ field: 'id' }, comparisonTypes.Equals, ids);
	} else if (searchStr) {
		rb.addFilter({ field: 'name' }, comparisonTypes.Search, searchStr);
	}
	rb.fields = ['id', 'name'];
	rb.addSort('name', true);
	rb.limit = 50;
	return ProjectResource.find(rb.build()).then(({ data }) =>
		data.map((c: Project) => ({ id: c.id.toString(), title: c.name }))
	);
};

const PRODUCT_CATEGORY_ID_PREFIX = 'cat-';

const isCategoryId = (id: number | string) => (id + '').startsWith(PRODUCT_CATEGORY_ID_PREFIX);
const parseCategoryId = (id: number | string) => Number((id + '').slice(PRODUCT_CATEGORY_ID_PREFIX.length));

// Categories come before products so they aren't buried below the product hits
export const createProductFetcher = () => async (searchStr: string, ids?: (number | string)[]) => {
	const search = searchStr.toLowerCase();
	const categories = AppService.getProductCategories()
		.filter(c =>
			ids?.length ? ids.includes(PRODUCT_CATEGORY_ID_PREFIX + c.id) : c.name.toLowerCase().includes(search)
		)
		.map(c => ({ id: PRODUCT_CATEGORY_ID_PREFIX + c.id, title: c.name, icon: 'clone' as const }))
		.sort((a, b) => a.title.localeCompare(b.title));

	const productIds = ids?.filter(id => !isCategoryId(id));
	if (ids?.length && !productIds?.length) {
		return categories;
	}

	const rb = new RequestBuilder();
	rb.addFilter(ProductAttributes.active, comparisonTypes.Equals, 1);
	if (productIds?.length) {
		rb.addFilter(ProductAttributes.id, comparisonTypes.Equals, productIds);
	} else if (searchStr) {
		rb.addFilter(ProductAttributes.name, comparisonTypes.Search, searchStr);
	}
	rb.fields = ['id', 'name'];
	rb.addSort('name', true);
	rb.limit = 50;
	const { data }: { data: Product[] } = await ProductResource.find(rb.build());
	return [...categories, ...data.map(p => ({ id: p.id.toString(), title: p.name }))];
};

// Product categories are picked as `cat-<id>` beside product ids; "is" matches any of them, "is not" none
export const buildProductOrCategoryFilter = (
	filter: ListViewFilter,
	rb: RequestBuilder,
	getField: (field: string) => { field: string }
) => {
	const values: (number | string)[] = Array.isArray(filter.value) ? filter.value : [];
	const categoryIds = values.filter(isCategoryId).map(parseCategoryId);
	const clauses = [
		{ field: 'orderRow.product.id', ids: values.filter(v => !isCategoryId(v)) },
		{
			field: 'product.category.id',
			ids: [...new Set([...categoryIds, ...findAllChildren(AppService.getProductCategories(), categoryIds)])]
		}
	].filter(clause => clause.ids.length);

	if (filter.comparisonType === 'NotEquals') {
		clauses.forEach(clause => {
			const notGroup = rb.groupBuilder();
			notGroup.isNotFilter();
			notGroup.addFilter(getField(clause.field), comparisonTypes.Equals, clause.ids);
			notGroup.done();
		});
	} else if (clauses.length === 1) {
		rb.addFilter(getField(clauses[0].field), comparisonTypes.Equals, clauses[0].ids);
	} else if (clauses.length) {
		const or = rb.orBuilder();
		clauses.forEach(clause => {
			or.next();
			or.addFilter(getField(clause.field), comparisonTypes.Equals, clause.ids);
		});
		or.done();
	}
};

export const fixOrderFilters = (filters: EntityFilter): EntityFilter => {
	const result = { ...filters };
	TypedObject.keys(result).forEach(key => {
		const entityFilters = result[key];
		if (!entityFilters) {
			return;
		}

		let changed = false;
		const fixedEntityFilters = { ...entityFilters };

		// The Angular search saved product picks as objects
		const productFilter = fixedEntityFilters.OrderRowProduct;
		if (productFilter?.value?.[0]?.id !== undefined) {
			fixedEntityFilters.OrderRowProduct = {
				...productFilter,
				value: productFilter.value.map((p: { id: number; isCategory?: boolean }) =>
					p.isCategory ? PRODUCT_CATEGORY_ID_PREFIX + p.id : String(p.id)
				)
			};
			changed = true;
		}

		(['OrderFilters', 'OpportunityFilters'] as const).forEach(filterKey => {
			const filter = fixedEntityFilters[filterKey];
			if (!filter?.value) return;
			const filterValue = filter.value;

			// Only fix if array contains objects
			if (filterValue?.OrderRowProduct?.value?.[0]?.id || filterValue?.OrderRowProductCategory?.value?.[0]?.id) {
				const productIds: number[] = [];
				const categoryIds: number[] = [];
				const selectedOrder: { id: number; isCategory?: boolean }[] = filterValue?.OrderRowProduct?.value || [];

				selectedOrder.forEach(product => {
					if (product.isCategory) {
						categoryIds.push(product.id);
					} else {
						productIds.push(product.id);
					}
				});

				const fixedValue = { ...filterValue };
				if (fixedValue.OrderRowProduct?.value) {
					fixedValue.OrderRowProduct = { ...fixedValue.OrderRowProduct, value: productIds };
				}
				if (fixedValue.OrderRowProductCategory?.value) {
					fixedValue.OrderRowProductCategory = {
						...fixedValue.OrderRowProductCategory,
						value: categoryIds,
						comparisonType: fixedValue.OrderRowProduct.comparisonType
					};
				}
				fixedEntityFilters[filterKey] = { ...filter, value: fixedValue };
				changed = true;
			}
		});

		if (changed) {
			result[key] = fixedEntityFilters;
		}
	});
	return result;
};
